import { Link, useLocation, useNavigate } from "react-router-dom";
import { ShoppingCart, User, Menu, Search, ChevronDown, Facebook, Instagram, Bell, X, Home, ShoppingBag, PenTool, Upload, Camera, Users, Layers, Mail, Sparkles, Wrench, Car } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import SettingsSelector from "@/components/SettingsSelector";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const Navbar = () => {
  const { cart } = useCart();
  const { user, isAuthenticated, logout } = useAuth();
  const { t } = useLanguage();
  
  // Notifications state
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const notificationRef = useRef(null);
  
  // Navigation dropdown state
  const [showNavDropdown, setShowNavDropdown] = useState(false);
  const [showCategoriesDropdown, setShowCategoriesDropdown] = useState(false);
  const navDropdownRef = useRef(null);
  const categoriesDropdownRef = useRef(null);
  
  // Dynamic categories state
  const [categories, setCategories] = useState([]);
  
  // Navigation items
  const NAV_ITEMS = [
    { href: "/", label: "Home Page", icon: Home },
    { href: "/account", label: "My Account", icon: User },
    { href: "/products", label: "Shop", icon: ShoppingBag },
    { href: "/new-products", label: "New Products", icon: Sparkles },
    { href: "/custom-sticker", label: "Custom Text Sticker", icon: PenTool },
    { href: "/my-design", label: "Create my own Graphics", icon: Upload },
    { href: "/graphics-supply-fitting", label: "Supply and Fit Graphics", icon: Wrench },
    { href: "/vehicle-wrapping", label: "Vehicle Wrapping", icon: Car },
    { href: "/mini-owners-club", label: "Ministripes Owners Forum", icon: Camera },
    { href: "/meet-mini-stripes", label: "Meet Ministripes UK", icon: Users },
    { href: "/stripes-application", label: "Installing your Graphics", icon: Layers },
    { href: "/contact", label: "Contact", icon: Mail },
  ];
  
  // Calculate cart total with VAT
  const cartTotal = cart.total * 1.2;
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const searchRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const isHomePage = location.pathname === "/";
  const isProductsPage = location.pathname.startsWith("/products");

  // Fetch categories dynamically
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/categories/all`);
        const allCategories = response.data;
        
        // Filter to get main categories (those with mini-stripes as parent or top-level relevant ones)
        const miniStripesRoot = allCategories.find(c => c.slug === 'mini-stripes');
        const mainCategories = allCategories.filter(cat => {
          // Get direct children of mini-stripes
          if (miniStripesRoot && cat.parent_id === miniStripesRoot.id) return true;
          // Also include PPF and PPF Tools
          if (cat.slug === 'ppf' || cat.slug === 'ppf-tools') return true;
          return false;
        });
        
        // Format for display
        const formattedCategories = [
          { href: "/products", label: "All Mini" },
          ...mainCategories.map(cat => ({
            href: `/products/${cat.slug}`,
            label: cat.name,
            isParent: cat.slug === 'ppf',
            isSubcategory: cat.slug === 'ppf-tools'
          }))
        ];
        
        setCategories(formattedCategories);
      } catch (error) {
        console.error("Error fetching categories:", error);
        // Fallback to static categories
        setCategories([
          { href: "/products", label: "All Mini" },
          { href: "/products/classic-mini", label: "Classic Mini" },
          { href: "/products/cooper-gen-1", label: "Cooper Gen 1" },
          { href: "/products/cooper-gen-2", label: "Cooper Gen 2" },
          { href: "/products/cooper-gen-3", label: "Cooper Gen 3" },
          { href: "/products/cooper-gen-4", label: "Cooper Gen 4" },
          { href: "/products/mini-gp", label: "Mini GP" },
          { href: "/products/countryman", label: "Countryman" },
          { href: "/products/clubman", label: "Clubman" },
          { href: "/products/ppf", label: "PPF" },
        ]);
      }
    };
    fetchCategories();
  }, []);

  // Debounced search
  useEffect(() => {
    if (searchQuery.trim().length < 2) {
      setSearchResults([]);
      setShowResults(false);
      return;
    }

    const timer = setTimeout(async () => {
      setIsSearching(true);
      try {
        const response = await fetch(`${API_URL}/api/products?search=${encodeURIComponent(searchQuery)}&limit=5`);
        if (response.ok) {
          const products = await response.json();
          setSearchResults(products);
          setShowResults(true);
        }
      } catch (error) {
        console.error("Search error:", error);
      } finally {
        setIsSearching(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch notifications for logged in users
  useEffect(() => {
    const fetchNotifications = async () => {
      if (!isAuthenticated) {
        setNotifications([]);
        setUnreadCount(0);
        return;
      }
      
      try {
        const token = localStorage.getItem('auth_token');
        const response = await axios.get(`${API_URL}/api/notifications`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.data) {
          setNotifications(response.data);
          setUnreadCount(response.data.filter(n => !n.read).length);
        }
      } catch (error) {
        // Notifications endpoint may not exist yet, silently fail
        console.log("Notifications not available");
      }
    };
    
    fetchNotifications();
    // Refresh notifications every 30 seconds
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [isAuthenticated]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowResults(false);
      }
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
      if (navDropdownRef.current && !navDropdownRef.current.contains(event.target)) {
        setShowNavDropdown(false);
      }
      if (categoriesDropdownRef.current && !categoriesDropdownRef.current.contains(event.target)) {
        setShowCategoriesDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/products?search=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery("");
      setShowResults(false);
    }
  };

  const handleProductClick = (productId) => {
    navigate(`/products/${productId}`);
    setSearchQuery("");
    setShowResults(false);
  };

  const navLinkClass = "text-sm font-medium text-gray-800 hover:text-black transition-all uppercase tracking-wide px-4 py-2 bg-white hover:bg-gray-100 rounded-full shadow-[2px_2px_0px_rgba(0,0,0,0.3)] hover:shadow-[1px_1px_0px_rgba(0,0,0,0.3)] hover:translate-x-[1px] hover:translate-y-[1px] active:shadow-none active:translate-x-[2px] active:translate-y-[2px]";

  // Track scroll position to show/hide floating nav button
  const [showFloatingNav, setShowFloatingNav] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      // Show floating nav button when scrolled past the navbar height (64px)
      setShowFloatingNav(window.scrollY > 64);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      {/* Static Navbar - Does not follow on scroll */}
      <nav className="w-full z-40 bg-black" data-testid="navbar">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Navigation Button & Social Icons - Left side */}
            <div className="flex items-center gap-3">
              {/* Navigation Dropdown - Only visible when not scrolled */}
              {!showFloatingNav && (
                <div className="relative" ref={navDropdownRef}>
                  <button
                    onClick={() => {
                      setShowNavDropdown(!showNavDropdown);
                      setShowCategoriesDropdown(false);
                    }}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-full shadow-sm transition-all ${
                      showNavDropdown 
                        ? "bg-black text-white border-white border" 
                        : "bg-white text-black hover:bg-black hover:text-white hover:border-white border border-gray-300"
                    }`}
                    data-testid="navbar-navigation-btn"
                  >
                    {showNavDropdown ? (
                      <X className="h-5 w-5" />
                    ) : (
                      <Menu className="h-5 w-5" />
                    )}
                    <span className="text-sm font-semibold hidden sm:inline">Navigation</span>
                  </button>
                  
                  {/* Dropdown Menu */}
                  {showNavDropdown && (
                    <div className="absolute top-full left-0 mt-2 bg-gray-100 rounded-2xl shadow-2xl border border-gray-200 overflow-hidden z-[100] p-3 min-w-[280px]">
                      <div className="grid grid-cols-1 gap-2 max-h-[70vh] overflow-y-auto">
                        {NAV_ITEMS.map((item) => {
                          const IconComponent = item.icon;
                          const isActive = location.pathname === item.href || 
                            (item.href !== "/" && location.pathname.startsWith(item.href));
                          
                          return (
                            <Link
                              key={item.href}
                              to={item.href}
                              onClick={() => setShowNavDropdown(false)}
                              className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all shadow-sm border ${
                                isActive 
                                  ? "bg-black text-white border-black" 
                                  : "bg-white text-gray-800 border-gray-200 hover:shadow-md hover:scale-[1.02]"
                              }`}
                            >
                              <IconComponent className="h-5 w-5 flex-shrink-0" />
                              <span className="text-sm font-semibold">{item.label}</span>
                            </Link>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Categories Dropdown - Only visible when not scrolled */}
              {!showFloatingNav && (
                <div className="relative" ref={categoriesDropdownRef}>
                  <button
                    onClick={() => {
                      setShowCategoriesDropdown(!showCategoriesDropdown);
                      setShowNavDropdown(false);
                    }}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-full shadow-sm transition-all ${
                      showCategoriesDropdown 
                        ? "bg-black text-white border-white border" 
                        : "bg-white text-black hover:bg-black hover:text-white hover:border-white border border-gray-300"
                    }`}
                    data-testid="navbar-categories-btn"
                  >
                    <ShoppingBag className="h-5 w-5" />
                    <span className="text-sm font-semibold hidden sm:inline">Categories</span>
                    <ChevronDown className={`h-4 w-4 transition-transform ${showCategoriesDropdown ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {/* Categories Dropdown Menu with Side Flyouts */}
                  {showCategoriesDropdown && (
                    <div className="absolute top-full left-0 mt-2 bg-gray-100 rounded-2xl shadow-2xl border border-gray-200 z-[100] p-3 min-w-[280px]">
                      {/* Mini Graphics */}
                      <div className="mb-3">
                        <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2 px-2">
                          Mini Graphics
                        </h3>
                        <div className="grid grid-cols-1 gap-2">
                          {/* Classic Mini */}
                          <div className="relative group/classic">
                            <Link
                              to="/products/classic-mini"
                              onClick={() => setShowCategoriesDropdown(false)}
                              className="flex items-center justify-between px-4 py-3 rounded-xl transition-all shadow-sm border bg-white text-gray-800 border-gray-200 hover:shadow-md hover:scale-[1.02]"
                            >
                              <span className="text-sm font-semibold">Classic Mini (1959 - 1986)</span>
                              <ChevronDown className="h-4 w-4 -rotate-90" />
                            </Link>
                            <div className="absolute left-full top-0 ml-2 bg-gray-100 rounded-xl shadow-xl border border-gray-200 p-2 min-w-[220px] opacity-0 invisible group-hover/classic:opacity-100 group-hover/classic:visible transition-all z-[101]">
                              <Link to="/products/badges-wheel-centres" onClick={() => setShowCategoriesDropdown(false)} className="flex items-center px-4 py-2.5 text-sm font-semibold rounded-xl bg-white text-gray-800 border border-gray-200 shadow-sm hover:shadow-md hover:scale-[1.02] transition-all mb-1">Badges & Wheel Centres</Link>
                              <Link to="/products/dealer-stickers" onClick={() => setShowCategoriesDropdown(false)} className="flex items-center px-4 py-2.5 text-sm font-semibold rounded-xl bg-white text-gray-800 border border-gray-200 shadow-sm hover:shadow-md hover:scale-[1.02] transition-all mb-1">Dealer Stickers</Link>
                              <Link to="/products/decal-stripe-kits" onClick={() => setShowCategoriesDropdown(false)} className="flex items-center px-4 py-2.5 text-sm font-semibold rounded-xl bg-white text-gray-800 border border-gray-200 shadow-sm hover:shadow-md hover:scale-[1.02] transition-all mb-1">Decal / Stripe Kits</Link>
                              <Link to="/products/engine-compartment-labels" onClick={() => setShowCategoriesDropdown(false)} className="flex items-center px-4 py-2.5 text-sm font-semibold rounded-xl bg-white text-gray-800 border border-gray-200 shadow-sm hover:shadow-md hover:scale-[1.02] transition-all mb-1">Engine / Compartment Labels</Link>
                              <Link to="/products/rover-mini" onClick={() => setShowCategoriesDropdown(false)} className="flex items-center px-4 py-2.5 text-sm font-semibold rounded-xl bg-white text-gray-800 border border-gray-200 shadow-sm hover:shadow-md hover:scale-[1.02] transition-all">Rover Mini</Link>
                            </div>
                          </div>

                          {/* Cooper Gen 1 */}
                          <Link to="/products/cooper-gen-1" onClick={() => setShowCategoriesDropdown(false)} className="flex items-center px-4 py-3 rounded-xl transition-all shadow-sm border bg-white text-gray-800 border-gray-200 hover:shadow-md hover:scale-[1.02]">
                            <span className="text-sm font-semibold">Cooper Gen 1 (2002-2006)</span>
                          </Link>

                          {/* Cooper Gen 2 */}
                          <Link to="/products/cooper-gen-2" onClick={() => setShowCategoriesDropdown(false)} className="flex items-center px-4 py-3 rounded-xl transition-all shadow-sm border bg-white text-gray-800 border-gray-200 hover:shadow-md hover:scale-[1.02]">
                            <span className="text-sm font-semibold">Cooper Gen 2 (2007-2013)</span>
                          </Link>

                          {/* Cooper Gen 2 Coupe/Roadster */}
                          <Link to="/products/cooper-gen-2-coupe-roadster" onClick={() => setShowCategoriesDropdown(false)} className="flex items-center px-4 py-3 rounded-xl transition-all shadow-sm border bg-white text-gray-800 border-gray-200 hover:shadow-md hover:scale-[1.02]">
                            <span className="text-sm font-semibold">Cooper Gen 2 Coupe / Roadster</span>
                          </Link>

                          {/* Cooper Gen 3 */}
                          <div className="relative group/gen3">
                            <Link
                              to="/products/cooper-gen-3"
                              onClick={() => setShowCategoriesDropdown(false)}
                              className="flex items-center justify-between px-4 py-3 rounded-xl transition-all shadow-sm border bg-white text-gray-800 border-gray-200 hover:shadow-md hover:scale-[1.02]"
                            >
                              <span className="text-sm font-semibold">Cooper Gen 3 (2014-2024)</span>
                              <ChevronDown className="h-4 w-4 -rotate-90" />
                            </Link>
                            <div className="absolute left-full top-0 ml-2 bg-gray-100 rounded-xl shadow-xl border border-gray-200 p-2 min-w-[220px] opacity-0 invisible group-hover/gen3:opacity-100 group-hover/gen3:visible transition-all z-[101]">
                              <Link to="/products/specifically-for-mini-f55-5-door" onClick={() => setShowCategoriesDropdown(false)} className="flex items-center px-4 py-2.5 text-sm font-semibold rounded-xl bg-white text-gray-800 border border-gray-200 shadow-sm hover:shadow-md hover:scale-[1.02] transition-all mb-1">Mini F55 5 Door</Link>
                              <Link to="/products/specifically-for-mini-f56-3-door" onClick={() => setShowCategoriesDropdown(false)} className="flex items-center px-4 py-2.5 text-sm font-semibold rounded-xl bg-white text-gray-800 border border-gray-200 shadow-sm hover:shadow-md hover:scale-[1.02] transition-all">Mini F56 3 Door</Link>
                            </div>
                          </div>

                          {/* Cooper Gen 4 */}
                          <div className="relative group/gen4">
                            <Link
                              to="/products/cooper-gen-4"
                              onClick={() => setShowCategoriesDropdown(false)}
                              className="flex items-center justify-between px-4 py-3 rounded-xl transition-all shadow-sm border bg-white text-gray-800 border-gray-200 hover:shadow-md hover:scale-[1.02]"
                            >
                              <span className="text-sm font-semibold">Cooper Gen 4 (2024+)</span>
                              <ChevronDown className="h-4 w-4 -rotate-90" />
                            </Link>
                            <div className="absolute left-full top-0 ml-2 bg-gray-100 rounded-xl shadow-xl border border-gray-200 p-2 min-w-[220px] opacity-0 invisible group-hover/gen4:opacity-100 group-hover/gen4:visible transition-all z-[101]">
                              <Link to="/products/mini-j01" onClick={() => setShowCategoriesDropdown(false)} className="flex items-center px-4 py-2.5 text-sm font-semibold rounded-xl bg-white text-gray-800 border border-gray-200 shadow-sm hover:shadow-md hover:scale-[1.02] transition-all mb-1">Mini J01</Link>
                              <Link to="/products/mini-f65" onClick={() => setShowCategoriesDropdown(false)} className="flex items-center px-4 py-2.5 text-sm font-semibold rounded-xl bg-white text-gray-800 border border-gray-200 shadow-sm hover:shadow-md hover:scale-[1.02] transition-all mb-1">Mini F65 5 Door</Link>
                              <Link to="/products/mini-f66" onClick={() => setShowCategoriesDropdown(false)} className="flex items-center px-4 py-2.5 text-sm font-semibold rounded-xl bg-white text-gray-800 border border-gray-200 shadow-sm hover:shadow-md hover:scale-[1.02] transition-all mb-1">Mini F66 3 Door</Link>
                              <Link to="/products/mini-f67" onClick={() => setShowCategoriesDropdown(false)} className="flex items-center px-4 py-2.5 text-sm font-semibold rounded-xl bg-white text-gray-800 border border-gray-200 shadow-sm hover:shadow-md hover:scale-[1.02] transition-all">F67 Cabriolet</Link>
                            </div>
                          </div>

                          {/* Mini GP */}
                          <Link to="/products/mini-gp" onClick={() => setShowCategoriesDropdown(false)} className="flex items-center px-4 py-3 rounded-xl transition-all shadow-sm border bg-white text-gray-800 border-gray-200 hover:shadow-md hover:scale-[1.02]">
                            <span className="text-sm font-semibold">Mini GP</span>
                          </Link>

                          {/* Countryman */}
                          <div className="relative group/countryman">
                            <Link
                              to="/products/countryman"
                              onClick={() => setShowCategoriesDropdown(false)}
                              className="flex items-center justify-between px-4 py-3 rounded-xl transition-all shadow-sm border bg-white text-gray-800 border-gray-200 hover:shadow-md hover:scale-[1.02]"
                            >
                              <span className="text-sm font-semibold">Countryman</span>
                              <ChevronDown className="h-4 w-4 -rotate-90" />
                            </Link>
                            <div className="absolute left-full top-0 ml-2 bg-gray-100 rounded-xl shadow-xl border border-gray-200 p-2 min-w-[240px] opacity-0 invisible group-hover/countryman:opacity-100 group-hover/countryman:visible transition-all z-[101]">
                              <Link to="/products/mini-r60" onClick={() => setShowCategoriesDropdown(false)} className="flex items-center px-4 py-2.5 text-sm font-semibold rounded-xl bg-white text-gray-800 border border-gray-200 shadow-sm hover:shadow-md hover:scale-[1.02] transition-all mb-1">R60 Countryman (2010-2016)</Link>
                              <Link to="/products/mini-countryman-f60" onClick={() => setShowCategoriesDropdown(false)} className="flex items-center px-4 py-2.5 text-sm font-semibold rounded-xl bg-white text-gray-800 border border-gray-200 shadow-sm hover:shadow-md hover:scale-[1.02] transition-all mb-1">F60 Countryman (2017-2022)</Link>
                              <Link to="/products/mini-u25" onClick={() => setShowCategoriesDropdown(false)} className="flex items-center px-4 py-2.5 text-sm font-semibold rounded-xl bg-white text-gray-800 border border-gray-200 shadow-sm hover:shadow-md hover:scale-[1.02] transition-all">U25 Countryman (2024+)</Link>
                            </div>
                          </div>

                          {/* Clubman */}
                          <Link to="/products/clubman" onClick={() => setShowCategoriesDropdown(false)} className="flex items-center px-4 py-3 rounded-xl transition-all shadow-sm border bg-white text-gray-800 border-gray-200 hover:shadow-md hover:scale-[1.02]">
                            <span className="text-sm font-semibold">Clubman</span>
                          </Link>

                          {/* Mini Information Labels */}
                          <Link to="/products/mini-information-labels" onClick={() => setShowCategoriesDropdown(false)} className="flex items-center px-4 py-3 rounded-xl transition-all shadow-sm border bg-white text-gray-800 border-gray-200 hover:shadow-md hover:scale-[1.02]">
                            <span className="text-sm font-semibold">Mini Information Labels</span>
                          </Link>
                        </div>
                      </div>

                      {/* Paint Protection */}
                      <div className="pt-3 border-t border-gray-300">
                        <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2 px-2">
                          Paint Protection
                        </h3>
                        <div className="grid grid-cols-1 gap-2">
                          <Link to="/products/ppf" onClick={() => setShowCategoriesDropdown(false)} className="flex items-center px-4 py-3 rounded-xl transition-all shadow-sm border bg-white text-gray-800 border-gray-200 hover:shadow-md hover:scale-[1.02]">
                            <span className="text-sm font-semibold">PPF Kits</span>
                          </Link>
                          <Link to="/products/ppf-tools" onClick={() => setShowCategoriesDropdown(false)} className="flex items-center px-4 py-3 rounded-xl transition-all shadow-sm border bg-white text-gray-800 border-gray-200 hover:shadow-md hover:scale-[1.02]">
                            <span className="text-sm font-semibold">PPF Tools</span>
                          </Link>
                        </div>
                      </div>

                      {/* Vinyl */}
                      <div className="pt-3 border-t border-gray-300">
                        <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2 px-2">
                          Vinyl
                        </h3>
                        <div className="grid grid-cols-1 gap-2">
                          <Link to="/products/vinyl-tools" onClick={() => setShowCategoriesDropdown(false)} className="flex items-center px-4 py-3 rounded-xl transition-all shadow-sm border bg-white text-gray-800 border-gray-200 hover:shadow-md hover:scale-[1.02]">
                            <span className="text-sm font-semibold">Vinyl Tools</span>
                          </Link>
                          {/* Wrap Vinyl with subcategories */}
                          <div className="relative group/wrap">
                            <Link
                              to="/products/wrap-vinyl"
                              onClick={() => setShowCategoriesDropdown(false)}
                              className="flex items-center justify-between px-4 py-3 rounded-xl transition-all shadow-sm border bg-white text-gray-800 border-gray-200 hover:shadow-md hover:scale-[1.02]"
                            >
                              <span className="text-sm font-semibold">Wrap Vinyl</span>
                              <ChevronDown className="h-4 w-4 -rotate-90" />
                            </Link>
                            <div className="absolute left-full top-0 ml-2 bg-gray-100 rounded-xl shadow-xl border border-gray-200 p-2 min-w-[240px] opacity-0 invisible group-hover/wrap:opacity-100 group-hover/wrap:visible transition-all z-[101]">
                              <Link to="/products/hx20000-matt" onClick={() => setShowCategoriesDropdown(false)} className="flex items-center px-4 py-2.5 text-sm font-semibold rounded-xl bg-white text-gray-800 border border-gray-200 shadow-sm hover:shadow-md hover:scale-[1.02] transition-all mb-1">HX20000 Matt</Link>
                              <Link to="/products/hx20000-gloss" onClick={() => setShowCategoriesDropdown(false)} className="flex items-center px-4 py-2.5 text-sm font-semibold rounded-xl bg-white text-gray-800 border border-gray-200 shadow-sm hover:shadow-md hover:scale-[1.02] transition-all mb-1">HX20000 Gloss</Link>
                              <Link to="/products/hx20000-metallic" onClick={() => setShowCategoriesDropdown(false)} className="flex items-center px-4 py-2.5 text-sm font-semibold rounded-xl bg-white text-gray-800 border border-gray-200 shadow-sm hover:shadow-md hover:scale-[1.02] transition-all mb-1">HX20000 Metallic</Link>
                              <Link to="/products/hx30000-chrome" onClick={() => setShowCategoriesDropdown(false)} className="flex items-center px-4 py-2.5 text-sm font-semibold rounded-xl bg-white text-gray-800 border border-gray-200 shadow-sm hover:shadow-md hover:scale-[1.02] transition-all mb-1">HX30000 Chrome</Link>
                              <Link to="/products/hx30000-carbon" onClick={() => setShowCategoriesDropdown(false)} className="flex items-center px-4 py-2.5 text-sm font-semibold rounded-xl bg-white text-gray-800 border border-gray-200 shadow-sm hover:shadow-md hover:scale-[1.02] transition-all mb-1">HX30000 Carbon Fibre</Link>
                              <Link to="/products/wrap-tools" onClick={() => setShowCategoriesDropdown(false)} className="flex items-center px-4 py-2.5 text-sm font-semibold rounded-xl bg-white text-gray-800 border border-gray-200 shadow-sm hover:shadow-md hover:scale-[1.02] transition-all">Wrap Tools</Link>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Search Bar - Only on Products page, next to Categories */}
              {isProductsPage && !showFloatingNav && (
                <div className="hidden md:block relative" ref={searchRef}>
                  <form onSubmit={handleSearch} className="flex items-center">
                    <div className="relative">
                      <input
                        type="text"
                        placeholder="Search for"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onFocus={() => searchResults.length > 0 && setShowResults(true)}
                        className="w-56 pl-4 pr-10 py-2.5 bg-white border border-gray-300 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        data-testid="navbar-products-search-input"
                      />
                      {searchQuery ? (
                        <button
                          type="button"
                          onClick={() => {
                            setSearchQuery('');
                            setShowResults(false);
                          }}
                          className="absolute right-1.5 top-1/2 -translate-y-1/2 h-7 w-7 flex items-center justify-center rounded-full bg-black text-white hover:bg-gray-800"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      ) : (
                        <div className="absolute right-1.5 top-1/2 -translate-y-1/2 h-7 w-7 flex items-center justify-center rounded-full bg-black">
                          <Search className="h-3.5 w-3.5 text-white" />
                        </div>
                      )}
                    </div>
                  </form>

                  {/* Search Results Dropdown */}
                  {showResults && searchResults.length > 0 && (
                    <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-xl shadow-xl z-50 max-h-80 overflow-y-auto">
                      {searchResults.map((product) => (
                        <div
                          key={product.id}
                          onClick={() => handleProductClick(product.id)}
                          className="flex items-center gap-3 p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                        >
                          {product.image_url ? (
                            <img
                              src={product.image_url}
                              alt={product.name}
                              className="w-12 h-12 object-cover rounded"
                            />
                          ) : (
                            <div className="w-12 h-12 bg-gray-200 rounded flex items-center justify-center">
                              <Search className="h-4 w-4 text-gray-400" />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">{product.name}</p>
                            <p className="text-sm text-gray-500">£{product.price?.toFixed(2)}</p>
                          </div>
                        </div>
                      ))}
                      <div
                        onClick={handleSearch}
                        className="p-3 text-center text-sm text-orange-600 hover:bg-gray-50 cursor-pointer font-medium"
                      >
                        View all results for "{searchQuery}"
                      </div>
                    </div>
                  )}
                </div>
              )}
          </div>

          {/* Desktop Navigation - Empty for now */}
          <div className="hidden md:flex items-center gap-3">
          </div>

          {/* Actions */}
          <div className="flex items-center gap-3">
            {/* Search Box - Hidden on homepage and products page */}
            {!isHomePage && !isProductsPage && (
              <div className="hidden md:block relative" ref={searchRef}>
                <form onSubmit={handleSearch} className="flex items-center">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <input
                      type="text"
                      placeholder={t('searchProducts')}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onFocus={() => searchResults.length > 0 && setShowResults(true)}
                      className="w-48 pl-9 pr-3 py-2 bg-white border border-border rounded-l-md text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      data-testid="navbar-search-input"
                    />
                  </div>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-black text-white text-sm font-medium uppercase tracking-wider rounded-r-full hover:bg-gray-800 transition-colors"
                    data-testid="navbar-search-button"
                  >
                    {t('search')}
                  </button>
                </form>

                {/* Search Results Dropdown */}
                {showResults && searchResults.length > 0 && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-border rounded-md shadow-lg z-50 max-h-80 overflow-y-auto" data-testid="search-results-dropdown">
                    {searchResults.map((product) => (
                      <div
                        key={product.id}
                        onClick={() => handleProductClick(product.id)}
                        className="flex items-center gap-3 p-3 hover:bg-gray-50 cursor-pointer border-b border-border last:border-b-0"
                      >
                        {product.image_url ? (
                          <img
                            src={product.image_url}
                            alt={product.name}
                            className="w-12 h-12 object-cover rounded"
                          />
                        ) : (
                          <div className="w-12 h-12 bg-gray-200 rounded flex items-center justify-center">
                            <Search className="h-4 w-4 text-gray-400" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{product.name}</p>
                          <p className="text-sm text-gray-500">£{product.price?.toFixed(2)}</p>
                        </div>
                      </div>
                  ))}
                  <div
                    onClick={handleSearch}
                    className="p-3 text-center text-sm text-primary hover:bg-gray-50 cursor-pointer font-medium"
                  >
                    View all results for "{searchQuery}"
                  </div>
                </div>
              )}

              {/* Loading indicator */}
              {isSearching && searchQuery.trim().length >= 2 && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-border rounded-md shadow-lg z-50 p-4 text-center text-sm text-gray-500">
                  Searching...
                </div>
              )}

              {/* No results */}
              {showResults && searchResults.length === 0 && searchQuery.trim().length >= 2 && !isSearching && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-border rounded-md shadow-lg z-50 p-4 text-center text-sm text-gray-500">
                  No products found
                </div>
              )}
            </div>
            )}

            {/* Settings Selector (Region, Language, Currency) */}
            <div className="hidden md:block group">
              <SettingsSelector className="flex items-center justify-center w-12 h-12 bg-white border border-gray-300 rounded-full text-black hover:bg-black hover:text-white hover:border-black transition-all" />
            </div>

            {/* Notification Bell - Only for logged in users */}
            {isAuthenticated && (
              <div className="relative" ref={notificationRef}>
                <button
                  onClick={() => setShowNotifications(!showNotifications)}
                  className="group flex items-center justify-center w-12 h-12 bg-white border border-gray-300 rounded-full text-black hover:bg-black hover:text-white hover:border-black transition-all relative"
                  data-testid="notification-bell"
                >
                  <Bell className="h-6 w-6 group-hover:text-white transition-colors" strokeWidth={1.5} />
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 rounded-full bg-red-500 text-white text-[11px] flex items-center justify-center font-bold shadow-sm animate-pulse">
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </button>
                
                {/* Notifications & Messages Dropdown */}
                {showNotifications && (
                  <div className="absolute right-0 top-full mt-2 w-80 bg-white border border-gray-200 rounded-xl shadow-xl z-[100] overflow-hidden">
                    <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
                      <h3 className="text-sm font-semibold text-gray-900">Notifications & Messages</h3>
                      {unreadCount > 0 && (
                        <span className="text-xs text-gray-500">{unreadCount} unread</span>
                      )}
                    </div>
                    <div className="max-h-80 overflow-y-auto">
                      {notifications.length === 0 ? (
                        <div className="px-4 py-8 text-center text-gray-500 text-sm">
                          <Bell className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                          <p>No notifications yet</p>
                          <p className="text-xs mt-1">You'll receive updates on comments, likes, messages, and forum activity</p>
                        </div>
                      ) : (
                        notifications.map((notification, index) => (
                          <Link
                            key={index}
                            to={notification.type === 'private_message' ? '/account?tab=notifications' : (notification.link || '#')}
                            onClick={() => setShowNotifications(false)}
                            className={`block px-4 py-3 border-b border-gray-100 hover:bg-gray-50 ${!notification.read ? 'bg-blue-50' : ''}`}
                          >
                            <div className="flex items-start gap-2">
                              {notification.type === 'private_message' && (
                                <span className="mt-0.5 px-1.5 py-0.5 bg-blue-100 text-blue-700 text-[10px] font-medium rounded">MSG</span>
                              )}
                              <div className="flex-1">
                                <p className="text-sm font-medium text-gray-900">{notification.title || notification.content}</p>
                                {notification.preview && (
                                  <p className="text-xs text-gray-600 mt-0.5 line-clamp-1">{notification.preview}</p>
                                )}
                                <p className="text-xs text-gray-500 mt-0.5">{notification.message}</p>
                                <p className="text-xs text-gray-400 mt-1">{notification.time}</p>
                              </div>
                            </div>
                          </Link>
                        ))
                      )}
                    </div>
                    <Link
                      to="/account?tab=notifications"
                      onClick={() => setShowNotifications(false)}
                      className="block px-4 py-3 text-center text-sm font-medium text-black hover:bg-gray-50 border-t border-gray-200"
                    >
                      View All Notifications & Messages
                    </Link>
                  </div>
                )}
              </div>
            )}

            {/* Cart/Basket - Same style as social tabs */}
            <Link to="/cart" className="group flex items-center justify-center w-12 h-12 bg-white border border-gray-300 rounded-full text-black hover:bg-black hover:text-white hover:border-black transition-all relative" data-testid="cart-link">
              <ShoppingCart className="h-6 w-6" strokeWidth={1.5} />
              {cart.item_count > 0 && (
                <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-red-500 text-white text-[10px] flex items-center justify-center font-bold border-2 border-white">
                  {cart.item_count}
                </span>
              )}
            </Link>

            {isAuthenticated ? (
              <div className="hidden md:flex items-center gap-2">
                <Link to="/account" data-testid="account-link" className="group flex items-center justify-center w-12 h-12 bg-white border border-gray-300 rounded-full hover:bg-black hover:border-black transition-all">
                  {user?.profile_image ? (
                    <img 
                      src={user.profile_image.startsWith('http') ? user.profile_image : `${process.env.REACT_APP_BACKEND_URL}${user.profile_image}`} 
                      alt={user.name || 'Profile'} 
                      className="h-10 w-10 rounded-full object-cover"
                    />
                  ) : (
                    <User className="h-6 w-6 text-black group-hover:text-white transition-colors" strokeWidth={1.5} />
                  )}
                </Link>
                {user?.is_admin && (
                  <Link to="/admin" data-testid="admin-link">
                    <Button variant="outline" size="sm" className="rounded-full uppercase tracking-wider text-xs bg-transparent text-gray-800 border-gray-800 hover:bg-gray-800 hover:text-white">
                      {t('admin') || 'Admin'}
                    </Button>
                  </Link>
                )}
              </div>
            ) : (
              <Link to="/login" className="hidden md:flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-300 rounded-full text-black hover:bg-black hover:text-white hover:border-black transition-all" data-testid="login-link">
                <User className="h-5 w-5" strokeWidth={1.5} />
                <span className="text-sm font-semibold">{t('signIn')}</span>
              </Link>
            )}

            {/* Mobile Menu */}
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild className="md:hidden">
                <Button variant="ghost" size="icon" className="text-gray-700 hover:bg-gray-200" data-testid="mobile-menu-btn">
                  <Menu className="h-5 w-5" strokeWidth={1.5} />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-80 bg-black text-white border-zinc-800">
                <div className="flex flex-col gap-6 mt-8">
                  {!isHomePage && (
                    <Link
                      to="/"
                      onClick={() => setMobileOpen(false)}
                      className="text-lg font-medium uppercase tracking-wide text-white"
                      data-testid="mobile-home-link"
                    >
                      Home
                    </Link>
                  )}
                  {!isProductsPage && (
                    <Link
                      to="/products"
                      onClick={() => setMobileOpen(false)}
                      className="text-lg font-bold uppercase tracking-wide bg-white text-black px-4 py-2 rounded text-center"
                    >
                      Shop
                    </Link>
                  )}
                  <span className="font-heading font-black text-xl tracking-tighter uppercase text-white">
                    Categories
                  </span>
                  {categories.map((category) => (
                    <Link
                      key={category.href}
                      to={category.href}
                      onClick={() => setMobileOpen(false)}
                      className="text-lg font-medium uppercase tracking-wide text-white/80 hover:text-white"
                    >
                      {category.label}
                    </Link>
                  ))}
                  <Link
                    to="/contact"
                    onClick={() => setMobileOpen(false)}
                    className="text-lg font-medium uppercase tracking-wide text-white/80 hover:text-white"
                  >
                    Contact
                  </Link>
                  <Link
                    to="/stripes-application"
                    onClick={() => setMobileOpen(false)}
                    className="text-lg font-medium uppercase tracking-wide text-white/80 hover:text-white"
                  >
                    Installing your Graphics
                  </Link>
                  <Link
                    to="/how-to-apply"
                    onClick={() => setMobileOpen(false)}
                    className="text-lg font-medium uppercase tracking-wide text-white/80 hover:text-white"
                  >
                    How to Apply
                  </Link>
                  {!isHomePage && (
                    <Link
                      to="/custom-sticker"
                      onClick={() => setMobileOpen(false)}
                      className="text-lg font-bold uppercase tracking-wide bg-white text-black px-4 py-2 rounded text-center mt-2"
                      data-testid="mobile-custom-sticker-link"
                    >
                      Custom Sticker
                    </Link>
                  )}
                  <Link
                    to="/meet-mini-stripes"
                    onClick={() => setMobileOpen(false)}
                    className="text-lg font-medium uppercase tracking-wide text-white/80 hover:text-white mt-2"
                    data-testid="mobile-meet-mini-stripes-link"
                  >
                    Meet Ministripes UK
                  </Link>
                  <div className="border-t border-zinc-700 pt-6 mt-4">
                    {isAuthenticated ? (
                      <>
                        <Link
                          to="/account"
                          onClick={() => setMobileOpen(false)}
                          className="flex items-center gap-3 text-lg font-medium uppercase tracking-wide mb-4 text-white"
                        >
                          {user?.profile_image ? (
                            <img 
                              src={user.profile_image.startsWith('http') ? user.profile_image : `${process.env.REACT_APP_BACKEND_URL}${user.profile_image}`} 
                              alt={user.name || 'Profile'} 
                              className="h-10 w-10 rounded-full object-cover border-2 border-white"
                            />
                          ) : (
                            <User className="h-6 w-6" />
                          )}
                          My Account
                        </Link>
                        {user?.is_admin && (
                          <Link
                            to="/admin"
                            onClick={() => setMobileOpen(false)}
                            className="block text-lg font-medium uppercase tracking-wide mb-4 text-white"
                          >
                            Admin Panel
                          </Link>
                        )}
                        <Button
                          onClick={() => {
                            logout();
                            setMobileOpen(false);
                          }}
                          variant="outline"
                          className="w-full rounded-full uppercase tracking-wider bg-transparent text-white border-white hover:bg-white hover:text-black"
                        >
                          Logout
                        </Button>
                      </>
                    ) : (
                      <div className="flex flex-col gap-2">
                        <Link to="/login" onClick={() => setMobileOpen(false)}>
                          <Button className="w-full rounded-full uppercase tracking-wider bg-white text-black hover:bg-gray-200">
                            Login
                          </Button>
                        </Link>
                        <Link to="/register" onClick={() => setMobileOpen(false)}>
                          <Button variant="outline" className="w-full rounded-full uppercase tracking-wider bg-transparent text-white border-white hover:bg-white hover:text-black">
                            Register
                          </Button>
                        </Link>
                      </div>
                    )}
                  </div>
                  
                  {/* Mobile Settings Selector */}
                  <div className="border-t border-zinc-700 pt-4 mt-4">
                    <span className="text-xs text-gray-400 uppercase tracking-wider mb-2 block">Settings</span>
                    <SettingsSelector className="mt-2" />
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </nav>

      {/* Floating Sticky Navigation Button - Appears when scrolled past navbar */}
      {showFloatingNav && (
        <div className="fixed top-4 left-4 z-50 flex items-center gap-3">
          {/* Navigation Button */}
          <div ref={navDropdownRef}>
            <button
              onClick={() => {
                setShowNavDropdown(!showNavDropdown);
                setShowCategoriesDropdown(false);
              }}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-full shadow-lg transition-all ${
                showNavDropdown 
                  ? "bg-black text-white border-white border" 
                  : "bg-white text-black hover:bg-black hover:text-white hover:border-white border border-gray-300"
              }`}
              data-testid="floating-navigation-btn"
            >
              {showNavDropdown ? (
                <X className="h-5 w-5" />
              ) : (
                <Menu className="h-5 w-5" />
              )}
              <span className="text-sm font-semibold hidden sm:inline">Navigation</span>
            </button>
            
            {/* Navigation Dropdown Menu */}
            {showNavDropdown && (
              <div className="absolute top-full left-0 mt-2 bg-gray-100 rounded-2xl shadow-2xl border border-gray-200 overflow-hidden z-[100] p-3 min-w-[280px]">
                <div className="grid grid-cols-1 gap-2 max-h-[70vh] overflow-y-auto">
                  {NAV_ITEMS.map((item) => {
                    const IconComponent = item.icon;
                    const isActive = location.pathname === item.href || 
                      (item.href !== "/" && location.pathname.startsWith(item.href));
                    
                    return (
                      <Link
                        key={item.href}
                        to={item.href}
                        onClick={() => setShowNavDropdown(false)}
                        className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all shadow-sm border ${
                          isActive 
                            ? "bg-black text-white border-black" 
                            : "bg-white text-gray-800 border-gray-200 hover:shadow-md hover:scale-[1.02]"
                        }`}
                      >
                        <IconComponent className="h-5 w-5 flex-shrink-0" />
                        <span className="text-sm font-semibold">{item.label}</span>
                      </Link>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Categories Button */}
          <div ref={categoriesDropdownRef}>
            <button
              onClick={() => {
                setShowCategoriesDropdown(!showCategoriesDropdown);
                setShowNavDropdown(false);
              }}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-full shadow-lg transition-all ${
                showCategoriesDropdown 
                  ? "bg-black text-white border-white border" 
                  : "bg-white text-black hover:bg-black hover:text-white hover:border-white border border-gray-300"
              }`}
              data-testid="floating-categories-btn"
            >
              <ShoppingBag className="h-5 w-5" />
              <span className="text-sm font-semibold hidden sm:inline">Categories</span>
              <ChevronDown className={`h-4 w-4 transition-transform ${showCategoriesDropdown ? 'rotate-180' : ''}`} />
            </button>
            
            {/* Categories Dropdown Menu with Side Flyouts - Same as navbar */}
            {showCategoriesDropdown && (
              <div className="absolute top-full left-0 mt-2 bg-gray-100 rounded-2xl shadow-2xl border border-gray-200 z-[100] p-3 min-w-[280px]">
                <div className="max-h-[80vh] overflow-y-auto" style={{overflow: 'visible'}}>
                {/* Mini Graphics */}
                <div className="mb-3">
                  <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2 px-2">
                    Mini Graphics
                  </h3>
                  <div className="grid grid-cols-1 gap-2">
                    {/* Classic Mini */}
                    <div className="relative" style={{position: 'relative'}}>
                      <Link
                        to="/products/classic-mini"
                        onClick={() => setShowCategoriesDropdown(false)}
                        className="peer flex items-center justify-between px-4 py-3 rounded-xl transition-all shadow-sm border bg-white text-gray-800 border-gray-200 hover:shadow-md hover:scale-[1.02]"
                      >
                        <span className="text-sm font-semibold">Classic Mini (1959 - 1986)</span>
                        <ChevronDown className="h-4 w-4 -rotate-90" />
                      </Link>
                      <div className="absolute left-full top-0 ml-2 bg-gray-100 rounded-xl shadow-xl border border-gray-200 p-2 min-w-[220px] invisible peer-hover:visible hover:visible z-[200]" style={{position: 'absolute', left: '100%'}}>
                        <Link to="/products/badges-wheel-centres" onClick={() => setShowCategoriesDropdown(false)} className="flex items-center px-4 py-2.5 text-sm font-semibold rounded-xl bg-white text-gray-800 border border-gray-200 shadow-sm hover:shadow-md hover:scale-[1.02] transition-all mb-1">Badges & Wheel Centres</Link>
                        <Link to="/products/dealer-stickers" onClick={() => setShowCategoriesDropdown(false)} className="flex items-center px-4 py-2.5 text-sm font-semibold rounded-xl bg-white text-gray-800 border border-gray-200 shadow-sm hover:shadow-md hover:scale-[1.02] transition-all mb-1">Dealer Stickers</Link>
                        <Link to="/products/decal-stripe-kits" onClick={() => setShowCategoriesDropdown(false)} className="flex items-center px-4 py-2.5 text-sm font-semibold rounded-xl bg-white text-gray-800 border border-gray-200 shadow-sm hover:shadow-md hover:scale-[1.02] transition-all mb-1">Decal / Stripe Kits</Link>
                        <Link to="/products/engine-compartment-labels" onClick={() => setShowCategoriesDropdown(false)} className="flex items-center px-4 py-2.5 text-sm font-semibold rounded-xl bg-white text-gray-800 border border-gray-200 shadow-sm hover:shadow-md hover:scale-[1.02] transition-all mb-1">Engine / Compartment Labels</Link>
                        <Link to="/products/rover-mini" onClick={() => setShowCategoriesDropdown(false)} className="flex items-center px-4 py-2.5 text-sm font-semibold rounded-xl bg-white text-gray-800 border border-gray-200 shadow-sm hover:shadow-md hover:scale-[1.02] transition-all">Rover Mini</Link>
                      </div>
                    </div>

                    {/* Cooper Gen 1 */}
                    <Link to="/products/cooper-gen-1" onClick={() => setShowCategoriesDropdown(false)} className="flex items-center px-4 py-3 rounded-xl transition-all shadow-sm border bg-white text-gray-800 border-gray-200 hover:shadow-md hover:scale-[1.02]">
                      <span className="text-sm font-semibold">Cooper Gen 1 (2002-2006)</span>
                    </Link>

                    {/* Cooper Gen 2 */}
                    <Link to="/products/cooper-gen-2" onClick={() => setShowCategoriesDropdown(false)} className="flex items-center px-4 py-3 rounded-xl transition-all shadow-sm border bg-white text-gray-800 border-gray-200 hover:shadow-md hover:scale-[1.02]">
                      <span className="text-sm font-semibold">Cooper Gen 2 (2007-2013)</span>
                    </Link>

                    {/* Cooper Gen 2 Coupe/Roadster */}
                    <Link to="/products/cooper-gen-2-coupe-roadster" onClick={() => setShowCategoriesDropdown(false)} className="flex items-center px-4 py-3 rounded-xl transition-all shadow-sm border bg-white text-gray-800 border-gray-200 hover:shadow-md hover:scale-[1.02]">
                      <span className="text-sm font-semibold">Cooper Gen 2 Coupe / Roadster</span>
                    </Link>

                    {/* Cooper Gen 3 */}
                    <div className="relative">
                      <Link
                        to="/products/cooper-gen-3"
                        onClick={() => setShowCategoriesDropdown(false)}
                        className="peer flex items-center justify-between px-4 py-3 rounded-xl transition-all shadow-sm border bg-white text-gray-800 border-gray-200 hover:shadow-md hover:scale-[1.02]"
                      >
                        <span className="text-sm font-semibold">Cooper Gen 3 (2014-2024)</span>
                        <ChevronDown className="h-4 w-4 -rotate-90" />
                      </Link>
                      <div className="absolute left-full top-0 ml-2 bg-gray-100 rounded-xl shadow-xl border border-gray-200 p-2 min-w-[220px] invisible peer-hover:visible hover:visible z-[200]">
                        <Link to="/products/specifically-for-mini-f55-5-door" onClick={() => setShowCategoriesDropdown(false)} className="flex items-center px-4 py-2.5 text-sm font-semibold rounded-xl bg-white text-gray-800 border border-gray-200 shadow-sm hover:shadow-md hover:scale-[1.02] transition-all mb-1">Mini F55 5 Door</Link>
                        <Link to="/products/specifically-for-mini-f56-3-door" onClick={() => setShowCategoriesDropdown(false)} className="flex items-center px-4 py-2.5 text-sm font-semibold rounded-xl bg-white text-gray-800 border border-gray-200 shadow-sm hover:shadow-md hover:scale-[1.02] transition-all">Mini F56 3 Door</Link>
                      </div>
                    </div>

                    {/* Cooper Gen 4 */}
                    <div className="relative">
                      <Link
                        to="/products/cooper-gen-4"
                        onClick={() => setShowCategoriesDropdown(false)}
                        className="peer flex items-center justify-between px-4 py-3 rounded-xl transition-all shadow-sm border bg-white text-gray-800 border-gray-200 hover:shadow-md hover:scale-[1.02]"
                      >
                        <span className="text-sm font-semibold">Cooper Gen 4 (2024+)</span>
                        <ChevronDown className="h-4 w-4 -rotate-90" />
                      </Link>
                      <div className="absolute left-full top-0 ml-2 bg-gray-100 rounded-xl shadow-xl border border-gray-200 p-2 min-w-[220px] invisible peer-hover:visible hover:visible z-[200]">
                        <Link to="/products/mini-j01" onClick={() => setShowCategoriesDropdown(false)} className="flex items-center px-4 py-2.5 text-sm font-semibold rounded-xl bg-white text-gray-800 border border-gray-200 shadow-sm hover:shadow-md hover:scale-[1.02] transition-all mb-1">Mini J01</Link>
                        <Link to="/products/mini-f65" onClick={() => setShowCategoriesDropdown(false)} className="flex items-center px-4 py-2.5 text-sm font-semibold rounded-xl bg-white text-gray-800 border border-gray-200 shadow-sm hover:shadow-md hover:scale-[1.02] transition-all mb-1">Mini F65 5 Door</Link>
                        <Link to="/products/mini-f66" onClick={() => setShowCategoriesDropdown(false)} className="flex items-center px-4 py-2.5 text-sm font-semibold rounded-xl bg-white text-gray-800 border border-gray-200 shadow-sm hover:shadow-md hover:scale-[1.02] transition-all mb-1">Mini F66 3 Door</Link>
                        <Link to="/products/mini-f67" onClick={() => setShowCategoriesDropdown(false)} className="flex items-center px-4 py-2.5 text-sm font-semibold rounded-xl bg-white text-gray-800 border border-gray-200 shadow-sm hover:shadow-md hover:scale-[1.02] transition-all">F67 Cabriolet</Link>
                      </div>
                    </div>

                    {/* Mini GP */}
                    <Link to="/products/mini-gp" onClick={() => setShowCategoriesDropdown(false)} className="flex items-center px-4 py-3 rounded-xl transition-all shadow-sm border bg-white text-gray-800 border-gray-200 hover:shadow-md hover:scale-[1.02]">
                      <span className="text-sm font-semibold">Mini GP</span>
                    </Link>

                    {/* Countryman */}
                    <div className="relative">
                      <Link
                        to="/products/countryman"
                        onClick={() => setShowCategoriesDropdown(false)}
                        className="peer flex items-center justify-between px-4 py-3 rounded-xl transition-all shadow-sm border bg-white text-gray-800 border-gray-200 hover:shadow-md hover:scale-[1.02]"
                      >
                        <span className="text-sm font-semibold">Countryman</span>
                        <ChevronDown className="h-4 w-4 -rotate-90" />
                      </Link>
                      <div className="absolute left-full top-0 ml-2 bg-gray-100 rounded-xl shadow-xl border border-gray-200 p-2 min-w-[240px] invisible peer-hover:visible hover:visible z-[200]">
                        <Link to="/products/mini-r60" onClick={() => setShowCategoriesDropdown(false)} className="flex items-center px-4 py-2.5 text-sm font-semibold rounded-xl bg-white text-gray-800 border border-gray-200 shadow-sm hover:shadow-md hover:scale-[1.02] transition-all mb-1">R60 Countryman (2010-2016)</Link>
                        <Link to="/products/mini-countryman-f60" onClick={() => setShowCategoriesDropdown(false)} className="flex items-center px-4 py-2.5 text-sm font-semibold rounded-xl bg-white text-gray-800 border border-gray-200 shadow-sm hover:shadow-md hover:scale-[1.02] transition-all mb-1">F60 Countryman (2017-2022)</Link>
                        <Link to="/products/mini-u25" onClick={() => setShowCategoriesDropdown(false)} className="flex items-center px-4 py-2.5 text-sm font-semibold rounded-xl bg-white text-gray-800 border border-gray-200 shadow-sm hover:shadow-md hover:scale-[1.02] transition-all">U25 Countryman (2024+)</Link>
                      </div>
                    </div>

                    {/* Clubman */}
                    <Link to="/products/clubman" onClick={() => setShowCategoriesDropdown(false)} className="flex items-center px-4 py-3 rounded-xl transition-all shadow-sm border bg-white text-gray-800 border-gray-200 hover:shadow-md hover:scale-[1.02]">
                      <span className="text-sm font-semibold">Clubman</span>
                    </Link>

                    {/* Mini Information Labels */}
                    <Link to="/products/mini-information-labels" onClick={() => setShowCategoriesDropdown(false)} className="flex items-center px-4 py-3 rounded-xl transition-all shadow-sm border bg-white text-gray-800 border-gray-200 hover:shadow-md hover:scale-[1.02]">
                      <span className="text-sm font-semibold">Mini Information Labels</span>
                    </Link>
                  </div>
                </div>

                {/* Paint Protection */}
                <div className="pt-3 border-t border-gray-300">
                  <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2 px-2">
                    Paint Protection
                  </h3>
                  <div className="grid grid-cols-1 gap-2">
                    <Link to="/products/ppf" onClick={() => setShowCategoriesDropdown(false)} className="flex items-center px-4 py-3 rounded-xl transition-all shadow-sm border bg-white text-gray-800 border-gray-200 hover:shadow-md hover:scale-[1.02]">
                      <span className="text-sm font-semibold">PPF Kits</span>
                    </Link>
                    <Link to="/products/ppf-tools" onClick={() => setShowCategoriesDropdown(false)} className="flex items-center px-4 py-3 rounded-xl transition-all shadow-sm border bg-white text-gray-800 border-gray-200 hover:shadow-md hover:scale-[1.02]">
                      <span className="text-sm font-semibold">PPF Tools</span>
                    </Link>
                  </div>
                </div>

                {/* Vinyl */}
                <div className="pt-3 border-t border-gray-300">
                  <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2 px-2">
                    Vinyl
                  </h3>
                  <div className="grid grid-cols-1 gap-2">
                    <Link to="/products/vinyl-tools" onClick={() => setShowCategoriesDropdown(false)} className="flex items-center px-4 py-3 rounded-xl transition-all shadow-sm border bg-white text-gray-800 border-gray-200 hover:shadow-md hover:scale-[1.02]">
                      <span className="text-sm font-semibold">Vinyl Tools</span>
                    </Link>
                    {/* Wrap Vinyl with subcategories */}
                    <div className="relative">
                      <Link
                        to="/products/wrap-vinyl"
                        onClick={() => setShowCategoriesDropdown(false)}
                        className="peer flex items-center justify-between px-4 py-3 rounded-xl transition-all shadow-sm border bg-white text-gray-800 border-gray-200 hover:shadow-md hover:scale-[1.02]"
                      >
                        <span className="text-sm font-semibold">Wrap Vinyl</span>
                        <ChevronDown className="h-4 w-4 -rotate-90" />
                      </Link>
                      <div className="absolute left-full top-0 ml-2 bg-gray-100 rounded-xl shadow-xl border border-gray-200 p-2 min-w-[240px] invisible peer-hover:visible hover:visible z-[200]">
                        <Link to="/products/hx20000-matt" onClick={() => setShowCategoriesDropdown(false)} className="flex items-center px-4 py-2.5 text-sm font-semibold rounded-xl bg-white text-gray-800 border border-gray-200 shadow-sm hover:shadow-md hover:scale-[1.02] transition-all mb-1">HX20000 Matt</Link>
                        <Link to="/products/hx20000-gloss" onClick={() => setShowCategoriesDropdown(false)} className="flex items-center px-4 py-2.5 text-sm font-semibold rounded-xl bg-white text-gray-800 border border-gray-200 shadow-sm hover:shadow-md hover:scale-[1.02] transition-all mb-1">HX20000 Gloss</Link>
                        <Link to="/products/hx20000-metallic" onClick={() => setShowCategoriesDropdown(false)} className="flex items-center px-4 py-2.5 text-sm font-semibold rounded-xl bg-white text-gray-800 border border-gray-200 shadow-sm hover:shadow-md hover:scale-[1.02] transition-all mb-1">HX20000 Metallic</Link>
                        <Link to="/products/hx30000-chrome" onClick={() => setShowCategoriesDropdown(false)} className="flex items-center px-4 py-2.5 text-sm font-semibold rounded-xl bg-white text-gray-800 border border-gray-200 shadow-sm hover:shadow-md hover:scale-[1.02] transition-all mb-1">HX30000 Chrome</Link>
                        <Link to="/products/hx30000-carbon" onClick={() => setShowCategoriesDropdown(false)} className="flex items-center px-4 py-2.5 text-sm font-semibold rounded-xl bg-white text-gray-800 border border-gray-200 shadow-sm hover:shadow-md hover:scale-[1.02] transition-all mb-1">HX30000 Carbon Fibre</Link>
                        <Link to="/products/wrap-tools" onClick={() => setShowCategoriesDropdown(false)} className="flex items-center px-4 py-2.5 text-sm font-semibold rounded-xl bg-white text-gray-800 border border-gray-200 shadow-sm hover:shadow-md hover:scale-[1.02] transition-all">Wrap Tools</Link>
                      </div>
                    </div>
                  </div>
                </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;
